import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../Firebase';
import { useUser } from '../../UserContext';

function Create() {
  const {userData,currentUser}= useUser()
  const [newQuestion, setNewQuestion] = useState('');
  const [answerType, setAnswerType] = useState('direct'); // 'direct' or 'multiple'
  const [directAnswer, setDirectAnswer] = useState('');
  const [options, setOptions] = useState({
    option1: '',
    option2: '',
    option3: '',
    option4: '',
    correctOption: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleOptionChange = (e) => {
    const { name, value } = e.target;
    setOptions(prev => ({ ...prev, [name]: value }));
  };

  const addFlashcard = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate inputs
      if (!newQuestion.trim()) {
        throw new Error('Please enter a question');
      }

      if (answerType === 'direct' && !directAnswer.trim()) {
        throw new Error('Please enter an answer');
      }

      if (answerType === 'multiple') {
        if (!options.option1 || !options.option2 || !options.correctOption) {
          throw new Error('Please provide at least two options and select the correct one');
        }
      }

      // Prepare data for Firebase
      const flashcardData = {
        question: newQuestion.trim(),
        answerType,
        userId: currentUser?.uid, 
        createdAt: new Date(),
        ...(answerType === 'direct' 
          ? { answer: directAnswer.trim() }
          : {
              options: {
                option1: options.option1.trim(),
                option2: options.option2.trim(),
                option3: options.option3.trim(),
                option4: options.option4.trim(),
              },
              correctOption: options.correctOption
            })
      };

      // Add to Firebase
      await addDoc(collection(db, 'flashcards'), flashcardData);

      // Reset form and show success
      setNewQuestion('');
      setDirectAnswer('');
      setOptions({
        option1: '',
        option2: '',
        option3: '',
        option4: '',
        correctOption: ''
      });
      setSuccess('Flashcard created successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <header className="text-center mb-10">
          <h1 className="text-4xl font-bold text-indigo-700 mb-3">Flashcard Creator</h1>
          <p className="text-lg text-gray-600">Build interactive learning materials with ease</p>
        </header>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <form onSubmit={addFlashcard} className="p-6 sm:p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-6">Create New Flashcard</h2>
              
              {/* Question Input */}
              <div className="mb-6">
                <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-2">
                  Question *
                </label>
                <textarea
                  id="question"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  placeholder="Enter your question here..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                  rows="3"
                  disabled={isLoading}
                  required
                />
              </div>

              {/* Answer Type Toggle */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Answer Type *
                </label>
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setAnswerType('direct')}
                    className={`px-4 py-2 rounded-lg ${answerType === 'direct' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    Direct Answer
                  </button>
                  <button
                    type="button"
                    onClick={() => setAnswerType('multiple')}
                    className={`px-4 py-2 rounded-lg ${answerType === 'multiple' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    Multiple Choice
                  </button>
                </div>
              </div>

              {/* Direct Answer Input */}
              {answerType === 'direct' && (
                <div className="mb-6">
                  <label htmlFor="directAnswer" className="block text-sm font-medium text-gray-700 mb-2">
                    Correct Answer *
                  </label>
                  <input
                    type="text"
                    id="directAnswer"
                    value={directAnswer}
                    onChange={(e) => setDirectAnswer(e.target.value)}
                    placeholder="Enter the correct answer..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                    disabled={isLoading}
                    required={answerType === 'direct'}
                  />
                </div>
              )}

              {/* Multiple Choice Options */}
              {answerType === 'multiple' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Options (mark the correct one) *
                  </label>
                  
                  <div className="space-y-3">
                    {[1, 2, 3, 4].map((num) => (
                      <div key={num} className="flex items-center">
                        <input
                          type="radio"
                          name="correctOption"
                          id={`option${num}`}
                          value={`option${num}`}
                          checked={options.correctOption === `option${num}`}
                          onChange={handleOptionChange}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                          disabled={!options[`option${num}`]}
                        />
                        <input
                          type="text"
                          name={`option${num}`}
                          value={options[`option${num}`]}
                          onChange={handleOptionChange}
                          placeholder={`Option ${num}`}
                          className="ml-3 flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Status Messages */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-500 text-green-700">
                {success}
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </span>
                ) : 'Create Flashcard'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Create;